/** Copyright © 2013-2025 DataYes, All Rights Reserved. */

import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { Prompt, useLocation } from 'react-router-dom';

interface PageInterceptorProviderProps {
  children: ReactNode;
}

export interface PageInterceptorAction {
  isIntercept: boolean;
  setIsIntercept: (v: boolean) => void;
}

const TEXT = '系统可能不会保存您所做的更改';

export const PageInterceptorContext = createContext<PageInterceptorAction>({
  isIntercept: false,
  setIsIntercept: () => {},
});

/**
 * 有些编辑的页面需要在用户作出操作前阻止用户退出，提供一个统一的context来对浏览器刷新、
 * 页面关闭，或者是页面内点击其他地方进行跳转进行约束
 */
export function PageInterceptorProvider({ children }: PageInterceptorProviderProps) {
  const [isIntercept, setIsIntercept] = useState(false);
  const { pathname } = useLocation();

  // 在浏览器层面进行拦截
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (e) {
        e.returnValue = TEXT;
      }
      return TEXT;
    };

    if (isIntercept) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      if (isIntercept) {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    };
  }, [isIntercept]);

  useEffect(() => {
    // path改变，清除intercept
    setIsIntercept(false);
  }, [pathname]);

  return (
    <PageInterceptorContext.Provider value={{ isIntercept, setIsIntercept }}>
      {/* 在Router层面进行拦截 */}
      <Prompt when={isIntercept} message={TEXT} />
      {children}
    </PageInterceptorContext.Provider>
  );
}

export function usePageInterceptor() {
  return useContext(PageInterceptorContext);
}
